import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { RenaultPopupDTO } from '../../../models/renault-popup-dto';
import { FileService } from '../../../services/file.service';
import { PopupService } from '../../../services/popup.service';
import { UserService } from 'src/app/services/user.service';
import { SimsurveyPollService } from 'src/app/services/simsurvey-poll.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-popup',
  templateUrl: './popup.component.html'
})
export class PopupComponent implements OnInit, OnDestroy {

  @ViewChild('popupModal', { read: TemplateRef }) popupModal: TemplateRef<any>;

  data: RenaultPopupDTO | null = null;
  htmlContent: SafeHtml | null = null;

  private navSub: Subscription;
  private modalRef: NgbModalRef | null = null;
  private modalIsOpen = false;
  private readonly sessionKey = 'myposventa_popup_shown';
  private popupsCache: RenaultPopupDTO[] = [];
  private popupsLoaded = false;
  private isLoading = false;

  constructor(
    private router: Router,
    private popupService: PopupService,
    private fileService: FileService,
    private modalService: NgbModal,
    private sanitizer: DomSanitizer,
    private userService: UserService,
    private simsurveyPollService: SimsurveyPollService
  ) { }

  ngOnInit(): void {
    // Intentar mostrar al cargar por primera vez (ruta actual)
    this.tryShowPopup(this.normalizePath(this.router.url));
    this.navSub = this.router.events
      .pipe(filter(evt => evt instanceof NavigationEnd))
      .subscribe(evt => {
        const nav = evt as NavigationEnd;
        const currentPath = this.normalizePath(nav.urlAfterRedirects || nav.url);
        this.tryShowPopup(currentPath);
      });
  }

  ngOnDestroy(): void {
    this.navSub?.unsubscribe();
    this.modalRef?.close();
  }

  private tryShowPopup(currentPath: string): void {
    if (this.isLoading) {
      return;
    }

    if (!this.popupsLoaded) {
      this.isLoading = true;
      this.popupService.getAll().subscribe(popups => {
        this.popupsCache = popups || [];
        this.popupsLoaded = true;
        this.isLoading = false;
        this.showPopupForPath(currentPath);
      }, () => {
        this.isLoading = false;
      });
      return;
    }

    this.showPopupForPath(currentPath);
  }

  private showPopupForPath(currentPath: string): void {
    if (this.modalIsOpen) {
      return;
    }

    const candidate = this.popupsCache.find(p => {
      const key = p.IdPopupInt?.toString();
      if (!key) {
        return false;
      }
      const urlOk = p.ForcePoll || this.urlMatches(p.Urls, currentPath);
      return urlOk && (!this.hasSeenPopup(key) || p.ForcePoll);
    });

    if (!candidate || !candidate.IdPopupInt) {
      return;
    }

    this.loadAndOpenPopup(candidate, candidate.IdPopupInt.toString());
  }

  private urlMatches(urls: string | null, currentPath: string): boolean {
    const list = (urls || '')
      .split(',')
      .map(u => this.normalizePath(u))
      .filter(u => u.length > 0);
    return list.some(u => u === currentPath);
  }

  private normalizePath(path: string | null | undefined): string {
    if (!path) {
      return '';
    }
    const clean = path.split('#')[0].split('?')[0].trim();
    if (clean === '/') {
      return '/';
    }
    return clean.endsWith('/') ? clean.slice(0, -1) : clean;
  }

  private loadAndOpenPopup(popup: RenaultPopupDTO, key: string): void {
    if (!popup.ImageFile?.FileUri) {
      return;
    }

    this.fileService.getFileDataTextByUrl(popup.ImageFile.FileUri).subscribe(html => {
      this.data = popup;
      this.htmlContent = this.sanitizer.bypassSecurityTrustHtml(html);
      this.modalRef = this.modalService.open(this.popupModal, { centered: true, size: 'lg', backdrop: 'static' });
      this.modalIsOpen = true;
      this.modalRef.result.finally(() => { this.modalIsOpen = false; });
      this.markPopupAsShown(key);
      this.popupService.trackPopup(popup.IdPopup).subscribe();
    }, () => {
      // Si falla la carga del HTML, no mostramos el popup ni marcamos la sesión.
    });
  }

  private markPopupAsShown(key: string): void {
    if (!key || this.data?.ForcePoll) {
      return;
    }
    const shown = this.getShownPopups();
    if (!shown.includes(key)) {
      shown.push(key);
      sessionStorage.setItem(this.sessionKey, JSON.stringify(shown));
    }
  }

  private hasSeenPopup(key: string): boolean {
    return this.getShownPopups().includes(key);
  }

  private getShownPopups(): string[] {
    const raw = sessionStorage.getItem(this.sessionKey);
    if (!raw) {
      return [];
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed as string[] : [];
    } catch {
      return [];
    }
  }

  closePopup(modal: NgbModalRef): void {
    if (this.data?.ForcePoll && this.data?.IdPollGrupoSimSurvey) {
      Swal.showLoading();
      this.simsurveyPollService.hasFilledSurvey(this.data.IdPollGrupoSimSurvey).subscribe(filled => {
        Swal.close();
        if (filled) {
          modal.close();
          window.location.reload();
        }
      });
      return;
    }
    modal.close();
  }

  onLinkClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A' && target.getAttribute('data') === 'surveyLinkElement') {
      event.preventDefault();
      this.addPollParticipant();
      return;
    }
    if (target.tagName === 'A' && target.getAttribute('href') !== '' && target.getAttribute('data') === 'routerLinkElement') {
      event.preventDefault();
      const href = target.getAttribute('href') || '';
      if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('//')) {
        const linkTarget = target.getAttribute('target') || '_blank';
        window.open(href, linkTarget);
      } else {
        if (this.modalRef) {
          this.modalRef.close();
        }
        this.router.navigate([href]);
      }
    }
  }

  addPollParticipant(): void {
    
    if (!this.data?.IdPollGrupoSimSurvey) {
      return;
    }

    this.simsurveyPollService.addParticipant(this.data.IdPollGrupoSimSurvey).subscribe({
      next: url => {
        if (url) {
          window.open(url, '_blank');
        }
      },
      error: err => console.error('SimSurvey AddParticipant error:', err)
    });
  }
}
